import React from 'react';
import { Check, ChevronRight } from 'lucide-react';

/**
 * The wizard stepper, shared by the envelope builder and the template editor.
 *
 * Extracted because both had an identical copy: the same markup diverging in
 * two places is how small styling bugs get fixed once and stay broken in the
 * other. Only the step labels differ, so they are a prop.
 *
 * Steps past `reachable` are visibly disabled rather than hidden — the user
 * should see where the flow goes, just not be able to skip ahead.
 */
export default function StepNav({ steps, step, reachable, onGo }) {
  return (
    <nav className='flex items-center justify-center gap-1'>
      {steps.map((s, i) => {
        const isActive = step === s.n;
        const isDone = s.n < step && s.n <= reachable;
        const canGo = s.n <= reachable;

        return (
          <React.Fragment key={s.n}>
            {i > 0 && <ChevronRight size={13} className='text-gray-300 flex-shrink-0' />}
            <button
              type='button'
              onClick={() => canGo && onGo(s.n)}
              disabled={!canGo}
              className={`flex items-center gap-2 px-2.5 py-1 rounded-md text-sm transition-colors ${
                isActive
                  ? 'text-blue-700 font-semibold'
                  : canGo
                    ? 'text-gray-600 hover:bg-gray-50'
                    : 'text-gray-300 cursor-not-allowed'
              }`}
            >
              <StepBadge n={s.n} isActive={isActive} isDone={isDone} canGo={canGo} />
              {s.label}
            </button>
          </React.Fragment>
        );
      })}
    </nav>
  );
}

/**
 * The numbered circle.
 *
 * `leading-none` is the load-bearing part: without it the glyph inherits the
 * button's line-height and sits off-centre inside the circle no matter what the
 * flex alignment says. The explicit 1:1 box plus `tabular-nums` keeps every
 * step the same width, so the row doesn't shift as the numbers change.
 */
function StepBadge({ n, isActive, isDone, canGo }) {
  const base =
    'inline-flex items-center justify-center flex-shrink-0 rounded-full leading-none tabular-nums font-semibold';

  if (isActive) {
    return (
      <span
        className={`${base} bg-blue-600 text-white`}
        style={{ width: 18, height: 18, fontSize: 10 }}
      >
        {n}
      </span>
    );
  }

  if (isDone) {
    return (
      <span
        className={`${base} bg-green-50 text-green-700 border border-green-200`}
        style={{ width: 18, height: 18 }}
      >
        <Check size={11} strokeWidth={3} />
      </span>
    );
  }

  return (
    <span
      className={`${base} border ${canGo ? 'border-gray-300 text-gray-500' : 'border-gray-200 text-gray-300'}`}
      style={{ width: 18, height: 18, fontSize: 10 }}
    >
      {n}
    </span>
  );
}
